/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * ActivityCard
 * ------------
 * One activity — a category people sign up for: the 21K, the sunrise yoga.
 *
 * Collapsible, because an event may carry up to ten of these and each one holds
 * a route or a venue plus its pit stops. Ten expanded cards is a page nobody can
 * navigate; ten summary lines is a list.
 *
 * Two rules the card enforces:
 *
 *  • **Choosing a type moves the location model, it does not fix it.** The pack
 *    seeds a `location_model` on each activity type, and it is right nearly
 *    always — but a run inside a stadium is venue-based and an open-water swim
 *    follows a route. So the type pre-selects and the organizer decides.
 *
 *  • **Only the chosen side renders.** `applyLocationModel` in the reducer
 *    clears the other side, so the batch can never carry a half-filled route row
 *    next to a venue row.
 */

import React from 'react';
import OrgInput from './OrgInput';
import OrgTextArea from './OrgTextArea';
import OrgSelect from './OrgSelect';
import { OrgCardPicker } from './OrgChoice';
import LocationSearchField from './LocationSearchField';
import { toOptions } from '../../athlohive/lookupOptions';
import { suggestedLocationModel } from '../../athlohive/activityLocationModel';
import OrgRichText from './OrgRichText';
import {
  validateActivity, MAX_EVENT_TITLE_LENGTH, MAX_EVENT_DESCRIPTION_LENGTH,
} from '../../athlohive/validateEventForm';
// Only the commented-out GPX field used these; CourseField now owns the upload.
// import FileUploadInput from '../../common/FileUploadInput';
// import { activityFileField } from '../../athlohive/pendingUploads';
import CourseField from './CourseField';

const LOCATION_OPTIONS = [
  {
    value: 'ROUTE_BASED', icon: '↝', title: 'Follows a route',
    description: 'Start and end points, a distance, and optional pit stops.',
  },
  {
    value: 'VENUE_BASED', icon: '⌂', title: 'Held at a venue',
    description: 'One place, one address. No route and no pit stops.',
  },
];

export default function ActivityCard({
  activity, index, errors, dispatch, lookups, expanded, onToggle,
  pendingUploads = {}, setPendingFile, readOnly = false,
}) {
  const activityErrors = errors || {};
  // Messages shown on screen stay gated on "has this step been attempted" —
  // marking fields red before somebody has reached them reads as blame.
  const broken = Object.keys(activityErrors).length > 0;

  // The badge asks a different question, so it uses a different source. `errors`
  // is empty until the step is attempted, so reading it here announced
  // "Complete" on an activity with nothing in it at all.
  const reallyIncomplete = Object.keys(validateActivity(activity)).length > 0;
  const status = !reallyIncomplete
    ? { className: 'ok', label: 'Complete' }
    : (broken
      ? { className: 'warn', label: 'Needs attention' }
      : { className: '', label: 'Not finished' });

  const typeOptions = toOptions(lookups.activityTypes);
  const unitOptions = toOptions(lookups.distanceUnits);
  const suggested = suggestedLocationModel(activity.activity_type_id, lookups.activityTypes);
  const typeLabel = typeOptions
    .find((option) => String(option.value) === String(activity.activity_type_id))?.label;

  const update = (field) => (value) => dispatch({
    type: 'updateActivity', index, field, value,
  });

  const updateRoute = (field) => (value) => dispatch({
    type: 'updateActivityNested', index, group: 'route', field, value,
  });

  const updateVenue = (field) => (value) => dispatch({
    type: 'updateActivityNested', index, group: 'venue', field, value,
  });

  /** An address + coordinates arriving together from LocationSearchField. */
  const applyPlace = (group, addressField, pointField) => ({ address, lat, lng }) => {
    dispatch({ type: 'updateActivityNested', index, group, field: addressField, value: address });
    dispatch({
      type: 'updateActivityPoint', index, group, field: pointField, axis: 'lat', value: lat,
    });
    dispatch({
      type: 'updateActivityPoint', index, group, field: pointField, axis: 'lng', value: lng,
    });
  };

  const onTypeChange = (value) => {
    update('activity_type_id')(value);
    // A suggestion, applied as the organizer changes type. They can move it back
    // in one click, and the picker shows which one was suggested and why.
    dispatch({
      type: 'updateActivity', index, field: 'location_model',
      value: suggestedLocationModel(value, lookups.activityTypes),
    });
  };

  const summary = [
    typeLabel,
    activity.location_model === 'ROUTE_BASED' ? 'Route' : 'Venue',
    activity.route?.total_distance ? `${activity.route.total_distance}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className={`orgf-act${broken ? ' broken' : ''}`}>
      <button
        type="button"
        className="orgf-acthead"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span className="orgf-actidx" aria-hidden="true">{index + 1}</span>
        <span style={{ minWidth: 0 }}>
          <span className="orgf-actname">{activity.name || `Activity ${index + 1}`}</span>
          <span className="orgf-actmeta">{summary || 'Not set up yet'}</span>
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          <span className={`org-pill ${status.className}`}>{status.label}</span>
          <span aria-hidden="true">{expanded ? '⌃' : '⌄'}</span>
        </span>
      </button>

      {expanded && (
        /*
          The fieldset starts HERE, inside the body — not around the whole card.
          The header above it is the expand toggle, and disabling that made a
          locked activity impossible to open and read, which is the one thing a
          locked event is still good for.
        */
        <fieldset className="orgf-fieldset orgf-actbody" disabled={readOnly}>
          <div className="orgf-grid orgf-g2" style={{ marginTop: 13 }}>
            <OrgInput
              label="Name" required
              value={activity.name} onChange={update('name')} error={activityErrors.name}
              placeholder="Half Marathon"
              // Uncapped until now, on an unbounded `text` column — the same
              // gap the event title had. Typing stops here; validateActivity
              // still catches a longer value loaded back in from an older row.
              maxLength={MAX_EVENT_TITLE_LENGTH}
            />
            <OrgSelect
              label="Activity type" required
              value={activity.activity_type_id} onChange={onTypeChange}
              options={typeOptions} error={activityErrors.activity_type_id}
              placeholder="Choose a type"
            />
          </div>

          <div style={{ marginTop: 13 }}>
            {/* <OrgTextArea
              label="Description"
              value={activity.description} onChange={update('description')}
              placeholder="Optional. What this category involves."
            /> */}
            {/* Rich text, like the event's own description — and capped for the
                first time: this column had no limit and no counter at all. */}
            <OrgRichText
              label="Description"
              value={activity.description} onChange={update('description')}
              error={activityErrors.description}
              placeholder="Optional. What this category involves."
              maxLength={MAX_EVENT_DESCRIPTION_LENGTH}
            />
          </div>

          {/*
            Per activity rather than per event, so a 5K and a 10K under the same
            event can fill up independently.

            The help text says it is not yet enforced because it is not, and an
            organizer who sets 200 here and then watches a 201st person register
            would otherwise reasonably think the platform had lost their data.
            Nothing writes event_registration.event_activity_id yet, so the
            capacity rule has no group to count and is skipped. The number is
            stored and shown; it just does not turn anybody away.
          */}
          <div className="orgf-grid orgf-g2" style={{ marginTop: 13 }}>
            <OrgInput
              label="Max participants" type="number" inputMode="numeric" min="1" step="1"
              value={activity.max_participants} onChange={update('max_participants')}
              error={activityErrors.max_participants}
              placeholder="Leave empty for unlimited"
              help={'Leave empty for unlimited. Not yet enforced at registration — the cap is '
                + 'saved and shown, but it does not close signups yet.'}
            />
            <OrgInput
              label="Entry fee" type="number" inputMode="decimal" min="0" step="0.01"
              value={activity.fee_amount} onChange={update('fee_amount')}
              error={activityErrors.fee_amount}
              placeholder="0"
              help={'0 means free. Payments are not live yet — the price is saved against '
                + 'this activity but nobody is charged.'}
            />
          </div>

          <div style={{ marginTop: 13 }}>
            <OrgCardPicker
              label="How is this activity located?" required
              value={activity.location_model}
              onChange={update('location_model')}
              options={LOCATION_OPTIONS}
              suggested={typeLabel ? suggested : undefined}
              suggestionNote={typeLabel ? `Suggested for ${typeLabel}` : undefined}
              error={activityErrors.location_model}
            />
          </div>

          {activity.location_model === 'ROUTE_BASED' && activity.route && (
            <div className="orgf-grid" style={{ marginTop: 15 }}>
              {activityErrors.route && <span className="orgf-err">{activityErrors.route}</span>}

              {/*
                The course comes first: start, finish and distance below are filled
                in from it, and stay editable.
              */}
              <CourseField
                route={activity.route}
                index={index}
                activityName={activity.name}
                dispatch={dispatch}
                unitOptions={unitOptions}
                pendingUploads={pendingUploads}
                setPendingFile={setPendingFile}
                readOnly={readOnly}
              />

              {/*
                `multiline` because start_address/end_address are `text` columns
                and a real start line reads "Gate 3, Sree Kanteerava Stadium,
                Kasturba Road, Sampangi Rama Nagar, Bengaluru 560001" — one line
                truncates it out of sight in a box the organizer cannot widen.
              */}
              <LocationSearchField
                label="Start line" showMap multiline readOnly={readOnly}
                address={activity.route.start_address}
                point={activity.route.start_point}
                onChange={applyPlace('route', 'start_address', 'start_point')}
                latLabel="Start latitude" lngLabel="Start longitude"
              />
              <LocationSearchField
                label="Finish line" showMap multiline readOnly={readOnly}
                address={activity.route.end_address}
                point={activity.route.end_point}
                onChange={applyPlace('route', 'end_address', 'end_point')}
                latLabel="Finish latitude" lngLabel="Finish longitude"
              />

              <div className="orgf-grid orgf-g2">
                <OrgInput
                  label="Total distance" required type="number" inputMode="decimal" min="0" step="0.01"
                  value={activity.route.total_distance}
                  onChange={updateRoute('total_distance')}
                />
                <OrgSelect
                  label="Measured in" required
                  value={activity.route.distance_measuring_unit_id}
                  onChange={updateRoute('distance_measuring_unit_id')}
                  options={unitOptions}
                  placeholder="Choose a unit"
                />
              </div>

              {/*
                Two ways in, one column. Most organizers have the .gpx as a file
                exported from Strava or Garmin, so uploading is the primary
                route; a few already host the course and want to point at it,
                which the link box keeps possible.
                
                If both are given, the upload wins — `applyUploadedUrls` writes
                the new URL over the field at save. That is the predictable way
                round: picking a file is the more deliberate act of the two.
              */}
              {/*
                Previous GPX-only field, replaced by CourseField below — which keeps
                the same upload (same pending-upload key, same file type) and adds
                drawing the course on the map, the line the event page draws, and
                distance / start / finish filled in from the course.

              <div className="orgf-field">
                <label htmlFor={`orgf-gpx-${index}`}>GPX route file</label>
                <FileUploadInput
                  is_public
                  value={activity.route.gpx_file_url}
                  onChange={updateRoute('gpx_file_url')}
                  pendingFile={pendingUploads[activityFileField(index, 'route.gpx_file_url')] || null}
                  onPendingFile={(file) => setPendingFile(
                    activityFileField(index, 'route.gpx_file_url'), file
                  )}
                  allowedFileTypes={['gpx']}
                  inputProps={{
                    id: `orgf-gpx-${index}`,
                    name: activityFileField(index, 'route.gpx_file_url'),
                  }}
                />
                <span className="orgf-help">
                  Optional. A GPX file is the course as GPS coordinates — participants download
                  it and load it onto a Garmin, Coros or Strava so their watch can navigate the
                  route and warn them if they go off course. Draw the route in Strava, Garmin
                  Connect or Plotaroute, export the GPX and upload it here.{' '}
                  <b>The map on the event page comes from the route line, not this file.</b>
                </span>
              </div>
              */}

              <OrgInput
                label="…or link to a GPX file already online" type="url"
                value={activity.route.gpx_file_url} onChange={updateRoute('gpx_file_url')}
                placeholder="https://connect.garmin.com/modern/course/12345678/gpx"
                help="Use this instead of uploading if the course already lives on Garmin, Strava or your own site."
              />

              <PitStops
                stops={activity.pit_stops || []}
                index={index}
                error={activityErrors.pit_stops}
                dispatch={dispatch}
                readOnly={readOnly}
              />
            </div>
          )}

          {activity.location_model === 'VENUE_BASED' && activity.venue && (
            <div className="orgf-grid" style={{ marginTop: 15 }}>
              {activityErrors.venue && <span className="orgf-err">{activityErrors.venue}</span>}

              <OrgInput
                label="Venue name" required
                value={activity.venue.venue_name} onChange={updateVenue('venue_name')}
                placeholder="Kanteerava Indoor Stadium"
              />
              <LocationSearchField
                label="Venue address" required showMap multiline readOnly={readOnly}
                address={activity.venue.venue_address}
                point={activity.venue.venue_point}
                onChange={applyPlace('venue', 'venue_address', 'venue_point')}
                latLabel="Venue latitude" lngLabel="Venue longitude"
              />
            </div>
          )}

          <p style={{ marginTop: 15, marginBottom: 0 }}>
            <button
              type="button"
              className="orgf-remove"
              onClick={() => dispatch({ type: 'removeActivity', index })}
            >
              Remove this activity
            </button>
          </p>
        </fieldset>
      )}
    </div>
  );
}

/**
 * Pit stops for a route: water points, aid stations, the turnaround.
 *
 * Every stop goes out in ONE batch operation rather than one each — the batch
 * caps at 50 operations, and a long route with a stop per kilometre would
 * otherwise exhaust it before the sponsors were written.
 */
function PitStops({ stops, index, error, dispatch, readOnly = false }) {
  return (
    <div>
      <p className="org-lab" style={{ marginBottom: 8 }}>
        Pit stops {stops.length > 0 && `· ${stops.length}`}
      </p>
      {error && <span className="orgf-err">{error}</span>}

      {stops.map((stop, stopIndex) => (
        <div key={stopIndex} className="orgf-manual" style={{ marginTop: 9 }}>
          <div className="orgf-grid orgf-g2">
            <OrgInput
              label={`Stop ${stopIndex + 1} name`}
              value={stop.name}
              onChange={(value) => dispatch({
                type: 'updatePitStop', index, stopIndex, field: 'name', value,
              })}
            />
            <OrgInput
              label={`Stop ${stopIndex + 1} description`}
              value={stop.description}
              onChange={(value) => dispatch({
                type: 'updatePitStop', index, stopIndex, field: 'description', value,
              })}
            />
          </div>
          {/*
            Was two bare latitude/longitude boxes, which asked the organizer for
            the one thing about a water station nobody knows. A stop is "the
            corner by the second flyover" — findable on a map in seconds,
            unanswerable as a pair of decimals. Manual entry is still there
            underneath for anyone who does have the numbers.
          */}
          <div style={{ marginTop: 11 }}>
            <LocationSearchField
              label={`Stop ${stopIndex + 1} location`} showMap readOnly={readOnly}
              address={stop.address || ''}
              point={stop.point}
              onChange={({ lat, lng, address }) => dispatch({
                type: 'updatePitStopPlace', index, stopIndex, lat, lng, address,
              })}
              latLabel={`Stop ${stopIndex + 1} latitude`}
              lngLabel={`Stop ${stopIndex + 1} longitude`}
            />
          </div>
          <p style={{ margin: '11px 0 0' }}>
            <button
              type="button"
              className="orgf-remove"
              onClick={() => dispatch({ type: 'removePitStop', index, stopIndex })}
            >
              Remove stop {stopIndex + 1}
            </button>
          </p>
        </div>
      ))}

      <button
        type="button"
        className="orgf-add"
        style={{ marginTop: 9 }}
        onClick={() => dispatch({ type: 'addPitStop', index })}
      >
        + Add a pit stop
      </button>
    </div>
  );
}
